import { api } from './api';

export interface LaborP3ReportParams {
  period?: string;
  start_date?: string;
  end_date?: string;
  source_file?: string;
  ingestion_run_id?: string;
}

export type LaborP3ReportResponse = Record<string, unknown>;

export interface LaborP3CalculateRequest {
  plant?: string | null;
  fiscal_year_period?: string;
  display_weeks?: number;
  current_date?: string | null;
  workbook_path?: string | null;
  validate_against_workbook?: boolean;
}

export type LaborP3CalculateResponse = Record<string, unknown>;

export interface LaborP3RefreshRequest extends LaborP3CalculateRequest {
  company_code?: string | null;
  clear_existing?: boolean;
  replace_existing?: boolean;
  run_sap_ingests?: boolean;
  generate_total_prod?: boolean;
  calculate?: boolean;
  sap_limit?: number;
  bi_sales_max_pages?: number;
  total_prod_pp_max_pages?: number;
}

export interface LaborP3RefreshResponse {
  status: string;
  steps: Array<Record<string, unknown>>;
  source_files?: Record<string, string> | null;
  refresh_metadata?: Record<string, unknown> | null;
  calculation?: LaborP3CalculateResponse | null;
}

export interface LaborP3RefreshJobResponse {
  run_id: string;
  status: string;
  plant?: string | null;
  fiscal_year_period?: string | null;
  result?: LaborP3RefreshResponse | null;
  error?: string | null;
  error_code?: string | null;
  debug_error?: Record<string, unknown> | null;
  created_at?: string | null;
  completed_at?: string | null;
}

export interface LaborPlantOption {
  plant: string;
  plant_name?: string | null;
  source: string;
}

export interface LaborPlantsResponse {
  source_model?: string | null;
  space?: string | null;
  source: string;
  filters: Record<string, unknown>;
  rows: LaborPlantOption[];
}

export interface LaborPerformanceNarrativeRequest {
  facts: Record<string, unknown>;
}

export interface LaborPerformanceNarrativeResponse {
  main: string;
  details: string;
  recommendations?: string;
}

export interface LaborPerformanceFeedbackRequest {
  plant?: string | null;
  fiscal_year_period?: string | null;
  view?: string | null;
  category?: string;
  comment: string;
  context?: Record<string, unknown>;
}

export interface LaborPerformanceFeedbackResponse {
  feedback_id: string;
  status: string;
  created_at: string;
}

const BASE = '/api/v1/labor-performance';
const REFRESH_POLL_INTERVAL_MS = 3000;
const REFRESH_MAX_WAIT_MS = 15 * 60 * 1000;
const REFRESH_FAILED_MESSAGE = "We couldn't refresh the report because SAP returned a temporary error while preparing production data. Please try again later.";
const SAP_RECONNECT_ERROR_CODES = new Set([
  'SAP_NOT_CONNECTED',
  'SAP_REFRESH_FAILED',
  'SAP_REFRESH_TOKEN_EXPIRED',
  'SAP_TOKEN_EXPIRED',
]);

export class LaborSapReconnectRequiredError extends Error {
  public readonly errorCode: string;

  constructor(errorCode: string, message = 'SAP Datasphere needs to be reconnected.') {
    super(message);
    this.name = 'LaborSapReconnectRequiredError';
    this.errorCode = errorCode;
  }
}

export class LaborReconciliationDownloadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LaborReconciliationDownloadError';
  }
}

function readErrorCode(value: unknown): string | null {
  if (typeof value === 'string') {
    try {
      return readErrorCode(JSON.parse(value));
    } catch {
      const match = value.match(/\bSAP_[A-Z0-9_]+\b/);
      return match?.[0] ?? null;
    }
  }
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const direct = record.error_code ?? record.errorCode;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  return (
    readErrorCode(record.detail) ||
    readErrorCode(record.error) ||
    readErrorCode(record.message) ||
    readErrorCode(record.response)
  );
}

function readErrorMessage(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return readErrorMessage(JSON.parse(trimmed)) ?? trimmed;
    } catch {
      return trimmed;
    }
  }
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const direct = record.message ?? record.detail ?? record.error;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  return readErrorMessage(record.detail) || readErrorMessage(record.error);
}

function clientSafeRefreshMessage(value: unknown): string | null {
  const message = readErrorMessage(value);
  if (!message) return null;
  if (/<(?:!doctype\s+html|html|head|body|title|table|script)\b/i.test(message)) {
    return null;
  }
  if (/\b(Cisco Secure Access|Umbrella|503 Service Unavailable|upstream server error|swg-proxy)\b/i.test(message)) {
    return null;
  }
  if (/\b(HTTPException|Traceback|Internal Server Error|Request failed|status\s*5\d\d|500)\b/i.test(message)) {
    return null;
  }
  return message;
}

async function readBlobErrorMessage(value: unknown): Promise<string | null> {
  if (!(value instanceof Blob)) return null;
  const text = await value.text();
  return clientSafeRefreshMessage(text);
}

export function getLaborSapReconnectErrorCode(error: unknown): string | null {
  if (error instanceof LaborSapReconnectRequiredError) return error.errorCode;
  const maybe = error as { response?: { data?: unknown; status?: number }; data?: unknown; message?: unknown };
  const code = readErrorCode(maybe?.response?.data) || readErrorCode(maybe?.data) || readErrorCode(error);
  if (code && SAP_RECONNECT_ERROR_CODES.has(code)) return code;
  const message = readErrorMessage(maybe?.response?.data) || readErrorMessage(maybe?.message);
  if (maybe?.response?.status === 409 && /SAP Datasphere|SAP session|refresh token/i.test(message ?? '')) {
    return 'SAP_REFRESH_FAILED';
  }
  return null;
}

export function isLaborSapReconnectError(error: unknown): boolean {
  return Boolean(getLaborSapReconnectErrorCode(error));
}

const sapRefreshPayload = (request: LaborP3RefreshRequest = {}) => ({
  plant: request.plant ?? '2800',
  company_code: request.company_code ?? null,
  fiscal_year_period: request.fiscal_year_period ?? '003/2027',
  display_weeks: request.display_weeks ?? 5,
  current_date: request.current_date ?? null,
  replace_existing: request.replace_existing ?? true,
  generate_total_prod: request.generate_total_prod ?? true,
  calculate: request.calculate ?? true,
  sap_limit: request.sap_limit ?? 5000,
  bi_sales_max_pages: request.bi_sales_max_pages ?? 20,
  total_prod_pp_max_pages: request.total_prod_pp_max_pages ?? 20,
});

const delay = (ms: number) => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});

async function waitForRefreshJob(
  runId: string,
  options: { signal?: AbortSignal; onStatus?: (job: LaborP3RefreshJobResponse) => void } = {},
): Promise<LaborP3RefreshResponse> {
  const startedAt = Date.now();
  while (Date.now() - startedAt < REFRESH_MAX_WAIT_MS) {
    if (options.signal?.aborted) throw new DOMException('Refresh polling aborted', 'AbortError');
    const { data } = await api.get<LaborP3RefreshJobResponse>(
      `${BASE}/refresh/jobs/${encodeURIComponent(runId)}`,
    );
    const status = String(data?.status ?? '').toUpperCase();
    options.onStatus?.(data);
    if (status === 'COMPLETED' && data.result) return data.result;
    if (status === 'FAILED') {
      const code = readErrorCode(data);
      if (code && SAP_RECONNECT_ERROR_CODES.has(code)) {
        throw new LaborSapReconnectRequiredError(code, readErrorMessage(data) ?? undefined);
      }
      throw new Error(clientSafeRefreshMessage(data) ?? REFRESH_FAILED_MESSAGE);
    }
    await delay(REFRESH_POLL_INTERVAL_MS);
  }
  throw new Error('The refresh is taking longer than expected. The current report is still available, and you can check back in a few minutes.');
}

export const laborPerformanceApi = {
  async getP3Report(params: LaborP3ReportParams = {}): Promise<LaborP3ReportResponse> {
    const { data } = await api.get<LaborP3ReportResponse>(`${BASE}/reports/p3`, { params });
    return data;
  },

  async calculate(request: LaborP3CalculateRequest = {}): Promise<LaborP3CalculateResponse> {
    const { data } = await api.post<LaborP3CalculateResponse>(`${BASE}/calculate`, {
      plant: request.plant ?? '2800',
      fiscal_year_period: request.fiscal_year_period ?? '003/2027',
      display_weeks: request.display_weeks ?? 5,
      current_date: request.current_date ?? null,
      workbook_path: request.workbook_path ?? null,
      validate_against_workbook: request.validate_against_workbook ?? false,
    });
    return data;
  },

  async refresh(request: LaborP3RefreshRequest = {}): Promise<LaborP3RefreshResponse> {
    const { data } = await api.post<LaborP3RefreshResponse>(`${BASE}/refresh`, {
      plant: request.plant ?? '2800',
      company_code: request.company_code ?? null,
      fiscal_year_period: request.fiscal_year_period ?? '003/2027',
      display_weeks: request.display_weeks ?? 5,
      current_date: request.current_date ?? null,
      workbook_path: request.workbook_path ?? null,
      clear_existing: request.clear_existing ?? true,
      replace_existing: request.replace_existing ?? true,
      run_sap_ingests: request.run_sap_ingests ?? true,
      generate_total_prod: request.generate_total_prod ?? true,
      calculate: request.calculate ?? true,
      sap_limit: request.sap_limit ?? 5000,
      bi_sales_max_pages: request.bi_sales_max_pages ?? 20,
      total_prod_pp_max_pages: request.total_prod_pp_max_pages ?? 20,
    });
    return data;
  },

  async createSapRefreshJob(request: LaborP3RefreshRequest = {}): Promise<LaborP3RefreshJobResponse> {
    try {
      const { data } = await api.post<LaborP3RefreshJobResponse>(
        `${BASE}/refresh/sap/jobs`,
        sapRefreshPayload(request),
      );
      return data;
    } catch (error) {
      const code = getLaborSapReconnectErrorCode(error);
      if (code) {
        throw new LaborSapReconnectRequiredError(code, readErrorMessage((error as { response?: { data?: unknown } })?.response?.data) ?? undefined);
      }
      throw error;
    }
  },

  async getRefreshJob(runId: string): Promise<LaborP3RefreshJobResponse> {
    const { data } = await api.get<LaborP3RefreshJobResponse>(
      `${BASE}/refresh/jobs/${encodeURIComponent(runId)}`,
    );
    return data;
  },

  async getActiveRefreshJob(request: LaborP3RefreshRequest = {}): Promise<LaborP3RefreshJobResponse | null> {
    const params: Record<string, string> = {};
    if (request.plant) params.plant = request.plant;
    if (request.fiscal_year_period) params.fiscal_year_period = request.fiscal_year_period;
    const { data } = await api.get<LaborP3RefreshJobResponse | null>(
      `${BASE}/refresh/jobs/active`,
      { params },
    );
    return data ?? null;
  },

  async getLatestRefreshJob(request: LaborP3RefreshRequest = {}): Promise<LaborP3RefreshJobResponse | null> {
    const params: Record<string, string> = {};
    if (request.plant) params.plant = request.plant;
    if (request.fiscal_year_period) params.fiscal_year_period = request.fiscal_year_period;
    const { data } = await api.get<LaborP3RefreshJobResponse | null>(
      `${BASE}/refresh/jobs/latest`,
      { params },
    );
    return data ?? null;
  },

  waitForRefreshJob,

  async refreshSap(request: LaborP3RefreshRequest = {}): Promise<LaborP3RefreshResponse> {
    const data = await this.createSapRefreshJob(request);
    if (String(data?.status ?? '').toUpperCase() === 'COMPLETED' && data.result) return data.result;
    return waitForRefreshJob(data.run_id);
  },

  async listPlants(): Promise<LaborPlantsResponse> {
    const { data } = await api.get<LaborPlantsResponse>(`${BASE}/plants`);
    return data;
  },

  async generateNarrative(
    request: LaborPerformanceNarrativeRequest,
    options: { signal?: AbortSignal } = {},
  ): Promise<LaborPerformanceNarrativeResponse> {
    const { data } = await api.post<LaborPerformanceNarrativeResponse>(
      `${BASE}/narrative`,
      request,
      { signal: options.signal },
    );
    return data;
  },

  async downloadReconciliationPackage(request: LaborP3CalculateRequest = {}): Promise<void> {
    let response;
    try {
      response = await api.get<Blob>(`${BASE}/reconciliation-package`, {
        params: {
          plant: request.plant ?? '2800',
          fiscal_year_period: request.fiscal_year_period ?? '003/2027',
        },
        responseType: 'blob',
      });
    } catch (error) {
      const maybe = error as { response?: { data?: unknown }; message?: unknown };
      const message = (
        await readBlobErrorMessage(maybe.response?.data) ||
        clientSafeRefreshMessage(maybe.response?.data) ||
        clientSafeRefreshMessage(maybe.message) ||
        "We couldn't download the reconciliation package. Please run Refresh Data and try again."
      );
      throw new LaborReconciliationDownloadError(message);
    }
    const disposition = response.headers['content-disposition'] ?? '';
    const match = /filename="?([^"]+)"?/i.exec(disposition);
    const filename = match?.[1] ?? `labor_reconciliation_package_${request.plant ?? '2800'}_${(request.fiscal_year_period ?? '003-2027').replace('/', '-')}.zip`;
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  async submitFeedback(request: LaborPerformanceFeedbackRequest): Promise<LaborPerformanceFeedbackResponse> {
    const { data } = await api.post<LaborPerformanceFeedbackResponse>(`${BASE}/feedback`, request);
    return data;
  },
};
